import { useQuery } from '@apollo/client';
import { Grid } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { GetSuperUsersResponse, GetSuperUsersVariables, SuperUserQueries } from '../../../../graphql/superUser.graphql';
import { SuperUser, superUserRoleToString } from '../../../../types/superUser';
import { toMap } from '../../../../util/map.util';
import { toFilterString } from '../../../../util/string.util';
import { AddButton } from '../../../components/Button';
import Table from '../../../components/Table';
import { Searchbar } from '../../../components/TextInput';
import LoadingPackage from '../../Common/LoadingPackage';
import CreateSuperUserModal from './Modals/CreateSuperUserModal';
import UpdateSuperUserModal from './Modals/UpdateSuperUserModal';

export default function AdministratorSuperUserPane() {
  const [superUsers, setSuperUsers] = useState<Map<string, SuperUser>>(new Map());
  const [filter, setFilter] = useState<string>('');
  const [selected, setSelected] = useState<string>('');

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [openUpdateModal, setOpenUpdateModal] = useState<boolean>(false);

  const { loading } = useQuery<GetSuperUsersResponse, GetSuperUsersVariables>(SuperUserQueries.getAll, {
    onCompleted: res => {
      setSuperUsers(new Map(toMap(res.superUsers.data, 'userId')));
    },
  });

  const handleFilter = (item: SuperUser) => {
    if (toFilterString(item.email).includes(filter) || toFilterString(item.role).includes(filter)) {
      return true;
    }
    return false;
  };

  const filteredSuperUsers = useMemo(() => {
    return [...superUsers.values()].filter(handleFilter);
  }, [filter, superUsers]);

  const headers = [
    {
      key: 'email',
      name: 'Email',
      text: (item: SuperUser) => {
        return item.email;
      },
    },
    {
      key: 'role',
      name: 'Role',
      text: (item: SuperUser) => {
        return superUserRoleToString(item.role);
      },
    },
  ];

  if (loading) {
    return (
      <Grid container height={'100%'} alignContent={'center'} justifyContent={'center'}>
        <Grid item className='fill-gray-300'>
          <LoadingPackage />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <CreateSuperUserModal
        open={openCreateModal}
        setOpen={setOpenCreateModal}
        superUsers={superUsers}
        setSuperUsers={setSuperUsers}
      />
      {selected && superUsers.get(selected) ? (
        <UpdateSuperUserModal
          open={openUpdateModal}
          setOpen={v => {
            setOpenUpdateModal(v);
            setSelected('');
          }}
          superUser={superUsers.get(selected)!}
          superUsers={superUsers}
          setSuperUsers={setSuperUsers}
        />
      ) : null}

      <div className='content-x content-b-screen flex h-full flex-col gap-4'>
        <div className='flex items-center justify-between gap-2'>
          <Searchbar value={filter} placeholder='Filter Super Users' onChange={v => setFilter(v)} />
          <AddButton onClick={() => setOpenCreateModal(true)} />
        </div>

        <Table
          onClick={item => {
            setSelected(item.userId);
            setOpenUpdateModal(true);
          }}
          items={filteredSuperUsers}
          headers={headers}
        />
      </div>
    </>
  );
}
